import { useEffect, useRef } from "react";

const BEFORE_INPUT_EVENT = "beforeinput";
const CHANGE_EVENT = "change";
const FONT_FORMAT_NAME = "font";
const FONT_SELECT_SELECTOR = ".ql-font";
const INSERT_REPLACEMENT_TEXT_INPUT_TYPE = "insertReplacementText";
const INSERT_TEXT_INPUT_TYPE = "insertText";
const NORMAL_FONT_VALUE = "";
const TEXT_PLAIN_MIME_TYPE = "text/plain";

const getEditor = (quillRef) => quillRef.current?.getEditor?.() ?? null;

const getFontValue = (fontSelect) =>
  fontSelect.value || NORMAL_FONT_VALUE;

const getFontFormatValue = (fontValue) =>
  fontValue === NORMAL_FONT_VALUE ? false : fontValue;

const hasStickyFont = (fontValue) => fontValue !== NORMAL_FONT_VALUE;

const applyFont = ({ fontValue, quill, userSource }) => {
  quill.format(FONT_FORMAT_NAME, getFontFormatValue(fontValue), userSource);
};

const getReplacementText = (event) => {
  if (typeof event.data === "string") return event.data;

  const transfer = event.dataTransfer;
  if (!transfer?.types?.includes(TEXT_PLAIN_MIME_TYPE)) return null;

  return transfer.getData(TEXT_PLAIN_MIME_TYPE);
};

const getInputText = (event) => {
  if (event.inputType === INSERT_TEXT_INPUT_TYPE) return event.data;
  if (event.inputType === INSERT_REPLACEMENT_TEXT_INPUT_TYPE) {
    return getReplacementText(event);
  }

  return null;
};

const buildTextFormats = ({ fontValue, quill, range }) => ({
  ...quill.getFormat(range),
  [FONT_FORMAT_NAME]: fontValue,
});

const insertTextWithDefaultFont = ({
  activeFontRef,
  Delta,
  event,
  quill,
  silentSource,
  userSource,
}) => {
  const fontValue = activeFontRef.current;
  const text = getInputText(event);
  if (!hasStickyFont(fontValue) || !text) return;

  const range = quill.getSelection(true);
  if (!range) return;

  const formats = buildTextFormats({ fontValue, quill, range });
  const delta = new Delta()
    .retain(range.index)
    .delete(range.length)
    .insert(text, formats);

  event.preventDefault();
  quill.updateContents(delta, userSource);
  quill.setSelection(range.index + text.length, silentSource);
};

const attachStickyFontListeners = ({
  activeFontRef,
  Delta,
  editorRoot,
  fontSelect,
  quill,
  silentSource,
  userSource,
}) => {
  const handleBeforeInput = (event) => {
    insertTextWithDefaultFont({
      activeFontRef,
      Delta,
      event,
      quill,
      silentSource,
      userSource,
    });
  };
  const handleFontChange = () => {
    activeFontRef.current = getFontValue(fontSelect);
    applyFont({ fontValue: activeFontRef.current, quill, userSource });
  };

  editorRoot.addEventListener(BEFORE_INPUT_EVENT, handleBeforeInput, true);
  fontSelect.addEventListener(CHANGE_EVENT, handleFontChange);

  return () => {
    editorRoot.removeEventListener(BEFORE_INPUT_EVENT, handleBeforeInput, true);
    fontSelect.removeEventListener(CHANGE_EVENT, handleFontChange);
  };
};

/**
 * Inserts typed text with the selected Quill font as the default text format.
 *
 * @param {{ Delta: Function, quillRef: import("react").RefObject, silentSource: string, userSource: string }} params
 * @returns {void}
 */
const useStickyQuillFont = ({ Delta, quillRef, silentSource, userSource }) => {
  const activeFontRef = useRef(NORMAL_FONT_VALUE);

  useEffect(() => {
    const quill = getEditor(quillRef);
    const fontSelect = document.querySelector(FONT_SELECT_SELECTOR);
    const editorRoot = quill?.root;
    if (!editorRoot || !fontSelect) return undefined;

    return attachStickyFontListeners({
      activeFontRef,
      Delta,
      editorRoot,
      fontSelect,
      quill,
      silentSource,
      userSource,
    });
  }, [Delta, quillRef, silentSource, userSource]);
};

export default useStickyQuillFont;
